import dayjs from 'dayjs';
import TikTokTooltipCard from 'components/DataVis/Charts/MultiPredictionTimeSeriesChart/components/TikTokTooltipCard';
import { formatVolume } from 'utils/helpers';
import { theme } from 'utils/theme';

/**
 * Hover tooltip for the entity-list charts: the dashboard's glass card
 * (TikTokTooltipCard) with DriversOfGrowthTooltip's compact layout, minus the
 * growth-drivers section. Receives MultiPredictionTimeSeriesChart's Tooltip
 * contract.
 *
 * The card spreads `...rest` after its own style, so the inline minWidth below
 * overrides its 277px dashboard minimum, which would dwarf a report tile.
 */

const TOOLTIP_WIDTH = 190;

export interface EntityListTooltipLabels {
    valueLabel: string;
    predictedValueLabel?: string;
    preserveNameCase?: boolean;
}

interface SeriesAccessors {
    xAccessor: (d: unknown) => string;
    yAccessor: (d: unknown) => number;
    predictionAccessor: (d: unknown) => boolean;
}

export interface EntityListTooltipProps {
    left?: number;
    top?: number;
    chartWidth?: number;
    chartHeight?: number;
    accessors?: SeriesAccessors;
    tooltipOpen?: boolean;
    tooltipData?: {
        datum?: unknown;
        name?: string;
        strokeColor?: string;
    } | null;
}

export const makeEntityListTooltip = ({
    valueLabel,
    predictedValueLabel,
    preserveNameCase,
}: EntityListTooltipLabels) =>
    function EntityListTooltip({
        left,
        top,
        chartWidth,
        chartHeight,
        accessors,
        tooltipOpen,
        tooltipData,
    }: EntityListTooltipProps) {
        if (!tooltipOpen || !tooltipData?.datum || !accessors) return null;

        const { datum } = tooltipData;
        // Without the label guard, a stray flag renders "Predicted undefined".
        const isPrediction =
            Boolean(accessors.predictionAccessor(datum)) &&
            Boolean(predictedValueLabel);
        const borderColor = isPrediction
            ? (theme.purple_1 as string)
            : tooltipData.strokeColor;

        return (
            <TikTokTooltipCard
                left={left}
                top={top}
                chartWidth={chartWidth}
                chartHeight={chartHeight}
                style={{ width: TOOLTIP_WIDTH, minWidth: 0 }}
            >
                <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-neutral-700 opacity-80">
                            {dayjs(
                                accessors.xAccessor(datum),
                                'YYYY-MM-DD',
                            ).format('MMM D, YYYY')}
                        </span>
                        <span
                            className={`text-xs font-semibold text-neutral-900 break-words ${
                                preserveNameCase ? '' : 'capitalize'
                            }`}
                        >
                            {tooltipData.name}
                        </span>
                    </div>
                    <div
                        className="flex flex-col gap-0.5 border-l-2 pl-2.5"
                        style={{ borderColor }}
                    >
                        <span className="text-sm font-bold uppercase text-neutral-900">
                            {formatVolume(accessors.yAccessor(datum))}
                        </span>
                        <span className="text-[11px] text-neutral-600">
                            {isPrediction ? predictedValueLabel : valueLabel}
                        </span>
                    </div>
                </div>
            </TikTokTooltipCard>
        );
    };
